import { ReactElement, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Link2, Users } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { convertToGroupConversation, shareConversation } from "@/api/api";
import { Conversation } from "@/api/models";
import { doTrackEvent } from "@/utils/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface ConversationShareDialogProps {
  conversation?: Conversation | null;
  trigger: ReactElement;
  trackEventItem: string;
  trackEventText: string;
}

const ConversationShareDialog = ({ conversation, trigger, trackEventItem, trackEventText }: ConversationShareDialogProps) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);
  const [groupLinkCopied, setGroupLinkCopied] = useState(false);
  const [shareMode, setShareMode] = useState<"view" | "group">(conversation?.isGroup ? "group" : "view");
  const [groupConvId, setGroupConvId] = useState<string | null>(conversation?.isGroup ? conversation?.id || null : null);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    setShareMode(conversation?.isGroup ? "group" : "view");
    setGroupConvId(conversation?.isGroup ? conversation?.id || null : null);
  }, [conversation?.id, conversation?.isGroup]);

  const resetDialogState = () => {
    setLinkCopied(false);
    setGroupLinkCopied(false);
    setIsConverting(false);
    setShareMode(conversation?.isGroup ? "group" : "view");
    setGroupConvId(conversation?.isGroup ? conversation?.id || null : null);
  };

  const onShare = () => {
    if (!conversation?.id) return;

    setLinkCopied(true);
    navigator.clipboard.writeText(`${window.location.origin}/share/${conversation.id}`);
    setTimeout(() => setLinkCopied(false), 3000);
    shareConversation(conversation.id);
    doTrackEvent("click", trackEventItem, trackEventText, conversation.id, "", "");
  };

  const onCreateGroupShare = async () => {
    if (isConverting || !conversation?.id) return;

    setIsConverting(true);
    try {
      const groupConversation = await convertToGroupConversation(conversation.id);
      if (groupConversation) {
        appStateContext?.dispatch({ type: "ADD_GROUP_CHAT", payload: groupConversation });
        appStateContext?.dispatch({ type: "DELETE_CONVERSATION", payload: conversation.id });
        appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
        navigate(`/gc/${groupConversation.id}`);
        setGroupConvId(groupConversation.id);
        navigator.clipboard.writeText(`${window.location.origin}/gc/join/${groupConversation.id}`);
        setGroupLinkCopied(true);
        setTimeout(() => setGroupLinkCopied(false), 3000);
      }
    } finally {
      setIsConverting(false);
    }
  };

  const onCopyGroupLink = () => {
    if (!groupConvId) return;

    navigator.clipboard.writeText(`${window.location.origin}/gc/join/${groupConvId}`);
    setGroupLinkCopied(true);
    setTimeout(() => setGroupLinkCopied(false), 3000);
  };

  if (!conversation?.id) return trigger;

  return (
    <Dialog onOpenChange={(open) => { if (!open) resetDialogState(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
        <DialogHeader>
          <DialogTitle className="mb-3">Share conversation</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 bg-base-100 p-1 rounded-xl">
          <button
            type="button"
            className={`flex-1 text-sm py-1.5 px-3 rounded-lg transition-colors ${shareMode === "view" ? "bg-secondary font-medium shadow-sm" : "hover:bg-neutral"}`}
            onClick={() => setShareMode("view")}
          >
            View only
          </button>
          <button
            type="button"
            className={`flex-1 text-sm py-1.5 px-3 rounded-lg transition-colors ${shareMode === "group" ? "bg-secondary font-medium shadow-sm" : "hover:bg-neutral"}`}
            onClick={() => setShareMode("group")}
          >
            Group conversation
          </button>
        </div>
        {shareMode === "view" ? (
          <>
            <DialogDescription className="text-sm">Others can view this conversation but cannot participate.</DialogDescription>
            <DialogFooter className="flex-col items-start gap-2">
              <div className="w-full border border-secondary rounded-[15px] text-xs select-none p-2 break-all">{`${window.location.origin}/share/${conversation.id}`}</div>
              <button type="button" className="btn btn-success btn-sm" onClick={onShare}>{linkCopied ? <><Check size={14} /> Copied</> : <><Link2 size={14} /> Copy Link</>}</button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogDescription className="text-sm">
              {groupConvId ? "Anyone with this link can join and participate." : "Create a group conversation that anyone with the link can join."}
            </DialogDescription>
            <DialogFooter className="flex-col items-start gap-2">
              {!groupConvId ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={isConverting}
                  onClick={onCreateGroupShare}
                >
                  {isConverting ? <span className="loading loading-spinner loading-xs" /> : <Users size={14} />}
                  Create & Copy Link
                </button>
              ) : (
                <>
                  <div className="w-full border border-secondary rounded-[15px] text-xs select-none p-2 break-all">{`${window.location.origin}/gc/join/${groupConvId}`}</div>
                  <button type="button" className="btn btn-success btn-sm" onClick={onCopyGroupLink}>{groupLinkCopied ? <><Check size={14} /> Copied</> : <><Link2 size={14} /> Copy Link</>}</button>
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConversationShareDialog;
